import React, { useState, useEffect, useCallback, useRef } from "react";
import ToastPopup from "./ToastPopup";
import { EditAdminChecklistProps, ControlWeights, createToastMessage, ToastMessage } from "../interFaces/interfaces";
import "../css/others/Popup.css";
import { apiCall } from "../main/ApiCall";
const EditAdminChecklist: React.FC<EditAdminChecklistProps> = ({ editableDetails, setEditableDetails, handleSaveChanges, onClose }) => {
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const [selectedControls, setSelectedControls] = useState<string[]>([]);
  const [controlOptions, setControlOptions] = useState<{ value: string; description: string }[]>([]);
  const [loadingControls, setLoadingControls] = useState<boolean>(false);
  const [controlWeights, setControlWeights] = useState<ControlWeights[]>([]);
  const [totalWeights, setTotalWeights] = useState<number>(0);
  const [isIntegratedChecklist, setIsIntegratedChecklist] = useState<string>("Select");
  const [selectedNormalControl, setSelectedNormalControl] = useState<string>("");
  const [isSaveEnabled, setIsSaveEnabled] = useState<boolean>(false);
  const [localName, setLocalName] = useState<string>("");
  const [localDescription, setLocalDescription] = useState<string>("");
  const [prefix, setPrefix] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);
  const restrictedChars = /[\\:*?"<>|/]/;
  const extractPrefixFromValue = (scopeValue: string): string => {
    let cleanValue = scopeValue.replace(/\s*Controls\s*/i, '');
    const prefix = cleanValue.trim().split(/\s+/)[0];
    return `${prefix}_`;
  };
  const showInvalidCharMessage = () => {
    setMessage(createToastMessage(`Special characters are not allowed - ${restrictedChars}`, "warning", 3000));
  };
  const validateRestrictedChars = (value: string) => {
    if (restrictedChars.test(value)) {
      showInvalidCharMessage();
      return false;
    }
    return true;
  };
  useEffect(() => {
    if (editableDetails) {
      setIsIntegratedChecklist(editableDetails.isIntegratedChecklist ? "Yes" : "No");
      setSelectedControls(editableDetails.controls || []);
      setSelectedNormalControl(editableDetails.scope || "");
      if (!editableDetails.isIntegratedChecklist && editableDetails.scope) {
        const scopePrefix = extractPrefixFromValue(editableDetails.scope);
        setPrefix(scopePrefix);
        setLocalName(editableDetails.templateName || scopePrefix);
      } else {
        setLocalName(editableDetails.templateName || "");
      }
      setLocalDescription(editableDetails.description || "");
      const normalizedWeights = editableDetails.controlWeights?.map((cw: any) => ({ control: cw.control, weight: cw.weight ?? cw.weights ?? cw.weighs ?? 0 })) || [];
      setControlWeights(normalizedWeights);
    }
  }, [editableDetails]);
  useEffect(() => {
    const fetchControlTypes = async () => {
      setLoadingControls(true);
      try {
        const response = await apiCall("get", "/api/enums/ControlsEnum");
        setControlOptions(response);
      } catch (error) {
        setMessage(createToastMessage("Failed to load control types", "error", 3000));
      } finally {
        setLoadingControls(false);
      }
    };
    fetchControlTypes();
  }, []);
  useEffect(() => {
    if (isIntegratedChecklist === "No" && selectedNormalControl) {
      const controlInfo = controlOptions.find(c => c.value === selectedNormalControl);
      const scopeValue = controlInfo?.value || selectedNormalControl;
      const newPrefix = extractPrefixFromValue(scopeValue);
      setPrefix(newPrefix);
      setLocalName((prevName) => {
        if (prevName.startsWith(newPrefix)) return prevName;
        const parts = prevName.split("_");
        const suffix = parts.length > 1 ? parts.slice(1).join("_") : "";
        return newPrefix + suffix;
      });
    }
  }, [isIntegratedChecklist, selectedNormalControl, controlOptions]);
  const syncToParent = useCallback(() => {
    setEditableDetails((prev: any) => ({ ...prev, templateName: localName, description: localDescription, isIntegratedChecklist: isIntegratedChecklist === "Yes", controls: isIntegratedChecklist === "Yes" ? selectedControls : [], controlWeights: isIntegratedChecklist === "Yes" && selectedControls.length > 0 ? controlWeights.filter((cw) => selectedControls.includes(cw.control)) : [], scope: isIntegratedChecklist === "No" ? selectedNormalControl : undefined }));
  }, [localName, localDescription, isIntegratedChecklist, selectedControls, controlWeights, selectedNormalControl, setEditableDetails]);
  useEffect(() => {
    const total = controlWeights.reduce((sum, cw) => sum + (cw.weight || 0), 0);
    setTotalWeights(total);
  }, [controlWeights]);
  useEffect(() => {
    if (selectedControls.length === 1) {
      setControlWeights([{ control: selectedControls[0], weight: 100 }]);
    } else if (selectedControls.length > 1) {
      setControlWeights((prev) => {
        const newWeights = selectedControls.map((control) => {
          const existing = prev.find((cw) => cw.control === control);
          if (existing) {
            return existing;
          }
          const originalWeight = editableDetails?.controlWeights?.find((cw: any) => cw.control === control);
          return { control, weight: originalWeight?.weight ?? originalWeight?.weights ?? originalWeight?.weighs ?? 0 };
        });
        return newWeights;
      });
    } else {
      setControlWeights([]);
    }
  }, [selectedControls, editableDetails]);
  const validateForm = useCallback(() => {
    const name = localName?.trim();
    const description = localDescription?.trim();
    if (!description) {
      setIsSaveEnabled(false);
      return;
    }
    if (name && name.length > 60) {
      setIsSaveEnabled(false);
      return;
    }
    if (isIntegratedChecklist === "Select") {
      setIsSaveEnabled(false);
      return;
    }
    if (isIntegratedChecklist === "Yes") {
      if (!name) {
        setIsSaveEnabled(false);
        return;
      }
      if (selectedControls.length <= 1) {
        setIsSaveEnabled(false);
        return;
      }
      if (selectedControls.length > 1) {
        if (totalWeights !== 100) {
          setIsSaveEnabled(false);
          return;
        }
        const hasZeroWeights = controlWeights.some((cw) => (cw.weight || 0) <= 0);
        if (hasZeroWeights) {
          setIsSaveEnabled(false);
          return;
        }
      }
    } else if (isIntegratedChecklist === "No") {
      if (!selectedNormalControl) {
        setIsSaveEnabled(false);
        return;
      }
      if (!localName.startsWith(prefix)) {
        setIsSaveEnabled(false);
        return;
      }
      if (localName.length <= prefix.length) {
        setIsSaveEnabled(false);
        return;
      }
    }
    setIsSaveEnabled(true);
  }, [localName, localDescription, isIntegratedChecklist, selectedControls, totalWeights, controlWeights, selectedNormalControl, prefix]);
  useEffect(() => {
    validateForm();
  }, [validateForm]);
  const handleWeightsChange = (control: string, value: string) => {
    if (value === "" || /^\d+$/.test(value)) {
      const numericValue = value === "" ? 0 : Math.max(0, Math.min(100, parseInt(value)));
      setControlWeights((prev) =>
        prev.map((cw) => (cw.control === control ? { ...cw, weight: numericValue } : cw))
      );
    }
  };
  const handleCheckboxChange = (controlValue: string) => {
    setSelectedControls((prev) =>
      prev.includes(controlValue) ? prev.filter((c) => c !== controlValue) : [...prev, controlValue]
    );
  };
  const handleRadioChange = (controlValue: string) => {
    setSelectedNormalControl(controlValue);
  };
  const handleIntegratedChecklistChange = (value: string) => {
    setIsIntegratedChecklist(value);
    if (value === "No") {
      setSelectedControls([]);
      setControlWeights([]);
      setPrefix("");
    } else if (value === "Yes") {
      setSelectedNormalControl("");
      setPrefix("");
    }
  };
  const handleNameChange = (value: string) => {
    if (isIntegratedChecklist === "Select") {
      setMessage(createToastMessage("Please select Integrated Checklist option (Yes/No) first", "warning", 3000));
      return;
    }
    if (isIntegratedChecklist === "Yes") {
      setLocalName(value);
    } else if (isIntegratedChecklist === "No") {
      if (!selectedNormalControl) {
        setMessage(createToastMessage("Please select a scope first", "warning", 3000));
        return;
      }
      if (value === "") {
        setLocalName(prefix);
      } else if (value.startsWith(prefix)) {
        setLocalName(value);
      } else {
        setLocalName(prefix + value);
        setMessage(createToastMessage("Scope prefix cannot be removed or modified", "warning", 2000));
      }
    }
  };
  const handleValidationAndSave = () => {
    const name = localName?.trim();
    const description = localDescription?.trim();
    if (!description) {
      setMessage(createToastMessage("Please fill all fields", "warning", 3000));
      return;
    }
    if (name && name.length > 60) {
      setMessage(createToastMessage("Checklist Name cannot exceed 60 characters", "warning", 3000));
      return;
    }
    if (isIntegratedChecklist === "Select") {
      setMessage(createToastMessage("Please select Integrated Checklist option", "warning", 3000));
      return;
    }
    if (isIntegratedChecklist === "Yes") {
      if (!name) {
        setMessage(createToastMessage("Please enter checklist name", "warning", 3000));
        return;
      }
      if (selectedControls.length <= 1) {
        setMessage(createToastMessage("Please select at least two controls for integrated checklist", "warning", 3000));
        return;
      }
      if (selectedControls.length > 1) {
        if (totalWeights !== 100) {
          setMessage(createToastMessage(`Total weights must be exactly 100%. Current total: ${totalWeights}%`, "warning", 3000));
          return;
        }
        const hasZeroWeights = controlWeights.some((cw) => (cw.weight || 0) <= 0);
        if (hasZeroWeights) {
          setMessage(createToastMessage("All selected controls must have weights greater than 0%", "warning", 3000));
          return;
        }
      }
    } else if (isIntegratedChecklist === "No") {
      if (!selectedNormalControl) {
        setMessage(createToastMessage("Please select a scope/control for the checklist", "warning", 3000));
        return;
      }
      if (!localName.startsWith(prefix)) {
        setMessage(createToastMessage("Checklist name must start with the scope prefix", "warning", 3000));
        return;
      }
      if (localName.length <= prefix.length) {
        setMessage(createToastMessage("Please add a name after the prefix", "warning", 3000));
        return;
      }
    }
    syncToParent();
    handleSaveChanges({ ...editableDetails, templateName: localName, description: localDescription, isIntegratedChecklist: isIntegratedChecklist === "Yes", controls: isIntegratedChecklist === "Yes" ? selectedControls : [], controlWeights: isIntegratedChecklist === "Yes" && selectedControls.length > 0 ? controlWeights.filter((cw) => selectedControls.includes(cw.control)) : [], scope: isIntegratedChecklist === "No" ? selectedNormalControl : undefined });
  };
  const getWeightsClass = () => {
    if (totalWeights === 100) return "weights-ok";
    if (totalWeights > 100) return "weights-over";
    return "weights-pending";
  };
  const handleRestrictedInputChange = (value: string, updater: (val: string) => void) => {
    if (restrictedChars.test(value)) {
      setMessage(createToastMessage(`Special characters are not allowed - ${restrictedChars}`, "warning", 3000));
      return;
    }
    updater(value);
  };
  const handleBeforeInput = (e: React.FormEvent<HTMLInputElement>) => {
    if (isIntegratedChecklist === "No" && prefix && selectedNormalControl) {
      const input = e.currentTarget;
      const cursorPosition = input.selectionStart || 0;
      const inputEvent = e as any;
      if (inputEvent.inputType === 'deleteContentBackward' ||
        inputEvent.inputType === 'deleteContentForward' ||
        inputEvent.inputType === 'deleteByCut') {
        if (cursorPosition <= prefix.length) {
          e.preventDefault();
          setMessage(createToastMessage("Scope prefix cannot be deleted", "warning", 2000));
        }
      }
    }
  };
  const handleSelect = (e: React.SyntheticEvent<HTMLInputElement>) => {
    if (isIntegratedChecklist === "No" && prefix && selectedNormalControl) {
      const input = e.currentTarget;
      if (input.selectionStart !== null && input.selectionStart < prefix.length) {
        input.setSelectionRange(prefix.length, input.selectionEnd);
      }
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isIntegratedChecklist === "No" && prefix && selectedNormalControl) {
      const input = e.currentTarget;
      const cursorPosition = input.selectionStart || 0;
      if ((e.key === 'Backspace' && cursorPosition <= prefix.length) ||
        (e.key === 'Delete' && cursorPosition < prefix.length)) {
        e.preventDefault();
        setMessage(createToastMessage("Scope prefix cannot be deleted", "warning", 2000));
      }
      if (e.key === 'ArrowLeft' && cursorPosition === prefix.length) {
        e.preventDefault();
      }
      if (e.key === 'Home') {
        e.preventDefault();
        input.setSelectionRange(prefix.length, prefix.length);
      }
    }
  };
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (isIntegratedChecklist === "No" && prefix && selectedNormalControl) {
      const input = e.currentTarget;
      const cursorPosition = input.selectionStart || 0;
      if (cursorPosition < prefix.length) {
        e.preventDefault();
        setMessage(createToastMessage("Cannot paste into the prefix area", "warning", 2000));
      }
    }
  };
  const isNameInputDisabled = () => {
    if (isIntegratedChecklist === "Select") return true;
    if (isIntegratedChecklist === "No" && !selectedNormalControl) return true;
    return false;
  };
  const getNamePlaceholder = () => {
    if (isIntegratedChecklist === "Select") {
      return "Please select Integrated Checklist option first";
    }
    if (isIntegratedChecklist === "No") {
      if (!selectedNormalControl) {
        return "Please select a scope first";
      }
      return `Enter name after ${prefix}`;
    }
    return "Enter checklist name";
  };
  return (
    <div className="editchecklist-overlay">
      <div className="editchecklist-content">
        <h3 className="editchecklist-title">Edit Checklist Item</h3>
        <label className="editchecklist-label">Checklist Name:
          <input ref={inputRef} type="text" className={`editchecklist-input ${isNameInputDisabled() ? "input-disabled" : ""}`} value={localName} onChange={(e) => { const value = e.target.value; if (!validateRestrictedChars(value)) return; handleNameChange(value) }} onKeyDown={handleKeyDown} onSelect={handleSelect} onBeforeInput={handleBeforeInput} onPaste={handlePaste} disabled={isNameInputDisabled()} placeholder={getNamePlaceholder()} />
          {isIntegratedChecklist === "No" && !selectedNormalControl && (
            <small className="checklist-warning">⚠ Please select a scope first to enable the checklist name field</small>
          )}
          {isIntegratedChecklist === "No" && selectedNormalControl && localName.length === prefix.length && (
            <small className="checklist-warning">⚠ Please add a name after the prefix</small>
          )}
        </label>
        <label className="editchecklist-label">Description:
          <textarea className={`editchecklist-textarea ${isIntegratedChecklist === "Select" ? "textarea-disabled " : ""}`} value={localDescription} onChange={(e) => { const value = e.target.value; if (!validateRestrictedChars(value)) return; handleRestrictedInputChange(value, setLocalDescription) }} disabled={isIntegratedChecklist === "Select"} />
        </label>
        <label className="editchecklist-label">Integrated Checklist:
          <select className="editchecklist-select" value={isIntegratedChecklist} onChange={(e) => handleIntegratedChecklistChange(e.target.value)} disabled={!editableDetails?.isIntegratedChecklist}>
            <option value="Select">Select an option</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </label>
        {isIntegratedChecklist !== "Select" && (
          <>
            <label className="editchecklist-subtitle">{isIntegratedChecklist === "Yes" ? "Select Controls (Select at least 2):" : "Select Scope:"}</label>
            <div className="editchecklist-controls">
              {loadingControls ? (
                <div className="editchecklist-loading">Loading control types...</div>
              ) : (
                <div className="editchecklist-control-list">
                  {controlOptions.map((control) => (
                    <label key={control.value} className="editchecklist-control-option">
                      {isIntegratedChecklist === "Yes" ? (
                        <input type="checkbox" value={control.value} checked={selectedControls.includes(control.value)} onChange={() => handleCheckboxChange(control.value)} />
                      ) : (
                        <input type="radio" name="normalControl" value={control.value} checked={selectedNormalControl === control.value} onChange={() => handleRadioChange(control.value)} />
                      )}{control.description} ({control.value})
                    </label>
                  ))}
                </div>
              )}
            </div>
            {isIntegratedChecklist === "Yes" && selectedControls.length > 0 && (
              <div className="editchecklist-weightage">
                <label className="editchecklist-weightage-label">Control Weights:</label>
                {selectedControls.length === 1 && (
                  <div className="editchecklist-warning">Please select at least two controls for integrated checklist</div>
                )}
                <div className={`editchecklist-total ${getWeightsClass()}`}>Total Weights: {totalWeights}%{totalWeights !== 100 && selectedControls.length > 1 && "(Must equal 100%)"}</div>
                {selectedControls.length === 1 ? (
                  <div className="editchecklist-single">
                    <p><strong>{controlOptions.find((c) => c.value === selectedControls[0])?.description}</strong> : 100% (Auto-assigned)</p>
                  </div>
                ) : (
                  <div className="editchecklist-multiple">
                    {selectedControls.map((control) => {
                      const controlInfo = controlOptions.find((c) => c.value === control);
                      const weightsObj = controlWeights.find((cw) => cw.control === control);
                      const weight = weightsObj ? weightsObj.weight || 0 : 0;
                      return (
                        <div key={control} className="editchecklist-weightage-item">
                          <span className="editchecklist-control-name">{controlInfo?.description} ({control})</span>
                          <div className="editchecklist-input-wrap">
                            <input type="text" inputMode="numeric" pattern="[0-9]*" value={weight} onChange={(e) => handleWeightsChange(control, e.target.value)} className="editchecklist-weightage-input" />
                            <span className="editchecklist-percent">%</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
        <div className="buttons">
          <button className="cancelbutton" onClick={onClose}>Cancel</button>
          <button className="savebutton" onClick={handleValidationAndSave} disabled={!isSaveEnabled}>Save</button>
        </div>
        {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
      </div>
    </div>
  );
};
export default EditAdminChecklist;